import { useEffect, useRef } from 'react';

/**
 * A textarea that grows to fit its content instead of scrolling inside itself.
 *
 * Extracted from IssueForm, where it already backed the issue description, so
 * the Story modal's description behaves the same way rather than carrying a
 * second copy that could drift.
 *
 * @param {number} minRows  Height floor, so an empty field still looks like a
 *   place to write rather than a single line.
 * @param {number} maxRows  Optional ceiling. Past it the field scrolls, which
 *   keeps a very long description from pushing the rest of a modal off screen.
 */
export default function AutoGrowTextarea({
  value,
  onChange,
  minRows = 3,
  maxRows = null,
  style,
  ...rest
}) {
  const ref = useRef(null);
  const linePx = 22;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset first: scrollHeight only shrinks if the element is allowed to.
    el.style.height = 'auto';
    const minH = minRows * linePx;
    const maxH = maxRows ? maxRows * linePx : Infinity;
    const next = Math.min(Math.max(el.scrollHeight, minH), maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > next ? 'auto' : 'hidden';
  }, [value, minRows, maxRows]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={minRows}
      style={{ ...style, resize: 'none', overflow: 'hidden', minHeight: minRows * linePx }}
      {...rest}
    />
  );
}
